import dayjs from 'dayjs';
import { useCallback, useState } from 'react';
import { matchPath, useLocation } from 'react-router';

import { CardStatus } from '#/plugins/paywall/models';
import { StorageKeys } from '#/service/constants';
import { Path } from '#/service/routes';
import { usePaywallStore } from '#/store/paywall/paywall.store';

import { useOfflineNetwork } from './useOfflineNetwork';

const DISMISS_DURATION_DAYS = 7;

interface UseBannerManagerReturn {
  banner:
    | 'offline'
    | 'paymentMethodExpiresSoon'
    | 'paymentMethodExpired'
    | null;
  dismissExpiryBanner?: () => void;
}

export const useBannerManager = (): UseBannerManagerReturn => {
  const { isOffline } = useOfflineNetwork();
  const { expiryStatus } = usePaywallStore();
  const { pathname } = useLocation();

  // expiry: the "dismissed until date" string we stored in localstorage
  const [dismissedUntilStored, setDismissedUntilStored] = useState(
    localStorage.getItem(StorageKeys.EXPIRY_DISMISSED_UNTIL)
  );

  // expiry: on dismiss, store the new date and update the state to re-render
  const dismissExpiryBanner = useCallback(() => {
    const until = dayjs().add(DISMISS_DURATION_DAYS, 'days').toISOString();
    localStorage.setItem(StorageKeys.EXPIRY_DISMISSED_UNTIL, until);
    setDismissedUntilStored(until);
  }, []);

  // offline: has priority over everything else
  if (isOffline) {
    return { banner: 'offline' };
  }

  // expiry: make it a date, default to very long ago
  const isDismissed = new Date() < new Date(dismissedUntilStored ?? 0);
  const isPlaybackPage = matchPath(Path.Playback, pathname);
  const isExpiredOrAboutTo =
    expiryStatus === CardStatus.AboutToExpire ||
    expiryStatus === CardStatus.Expired;

  // expiry: if not currently dismissed, and not on the playback page
  if (isExpiredOrAboutTo && !isDismissed && !isPlaybackPage) {
    return {
      banner:
        expiryStatus === CardStatus.AboutToExpire
          ? 'paymentMethodExpiresSoon'
          : 'paymentMethodExpired',
      dismissExpiryBanner,
    };
  }

  return { banner: null };
};
